import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { NAV, PLANITY_URL } from "../../data/content";

const EASE = [0.22, 1, 0.36, 1];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  const hashHref = (to) => (isHome ? `#${to}` : `/#${to}`);

  const linkClass =
    "group flex items-center justify-between py-5 border-b border-[#2b2a27]/10 font-serif-display text-4xl text-[#4b2018]";

  const desktopItem =
    "text-sm text-[#4b2018] hover:text-[#904234] transition-colors";

  return (
    <>
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#ece4d7]"
      >
        <div className="max-w-6xl mx-auto px-6 h-16 flex flex-row-reverse md:flex-row items-center justify-between">
          <Link
            to="/"
            data-testid="logo"
            className="font-serif-display text-xl md:text-2xl tracking-tight text-[#4b2018]"
          >
            Ninaa Beauty
          </Link>

          <nav className="hidden md:flex items-center gap-6 lg:gap-7">
            {NAV.map((n) => {
              if (n.type === "dropdown") {
                const Trigger = n.to ? Link : "button";
                const triggerProps = n.to
                  ? { to: n.to }
                  : { type: "button" };
                return (
                  <div key={n.label} className="relative group">
                    <Trigger
                      {...triggerProps}
                      data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                      className={`inline-flex items-center gap-1 ${desktopItem}`}
                    >
                      {n.label}
                      <ChevronDown
                        size={14}
                        className="text-[#b07a63] transition-transform duration-300 group-hover:rotate-180"
                      />
                    </Trigger>
                    {/* pont invisible + menu */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 z-50">
                      <div className="min-w-[232px] rounded-lg border border-[#ece4d7] bg-white/95 backdrop-blur-md shadow-[0_18px_40px_-24px_rgba(43,42,39,0.4)] py-2">
                        {n.children.map((c) => (
                          <Link
                            key={c.label}
                            to={c.to}
                            data-testid={`nav-sub-${c.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                            className="block px-5 py-2.5 text-sm text-[#4b2018] hover:bg-[#f6f1e9] hover:text-[#904234] transition-colors"
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return n.type === "route" ? (
                <Link
                  key={n.label}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className={desktopItem}
                >
                  {n.label}
                </Link>
              ) : (
                <a
                  key={n.label}
                  href={hashHref(n.to)}
                  data-testid={`nav-${n.to}`}
                  className={desktopItem}
                >
                  {n.label}
                </a>
              );
            })}
          </nav>

          <button
            data-testid="menu-open"
            aria-label="Ouvrir le menu"
            onClick={() => setOpen(true)}
            className="md:hidden text-[#4b2018]"
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 40 }}
            transition={{ duration: 0.55, ease: EASE }}
            className="md:hidden fixed inset-0 z-[80] bg-[#f6f1e9]/95 backdrop-blur-xl flex flex-col"
          >
            <div className="flex justify-between items-center px-6 h-16 border-b border-[#2b2a27]/10">
              <span className="font-serif-display text-xl text-[#4b2018]">
                Ninaa Beauty
              </span>
              <button
                data-testid="menu-close"
                aria-label="Fermer le menu"
                onClick={() => setOpen(false)}
                className="grid place-items-center h-10 w-10 rounded-full text-[#4b2018] hover:bg-white/70 transition-colors"
              >
                <X size={22} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto flex flex-col justify-center px-8">
              {NAV.map((n, i) => (
                <motion.div
                  key={n.label}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + i * 0.07, duration: 0.5, ease: EASE }}
                >
                  {n.type === "dropdown" ? (
                    <div className="border-b border-[#2b2a27]/10">
                      <button
                        type="button"
                        data-testid={`mobile-toggle-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                        onClick={() => setOpenSub((v) => (v === n.label ? null : n.label))}
                        className="w-full flex items-center justify-between py-5 font-serif-display text-4xl text-[#4b2018]"
                      >
                        {n.label}
                        <ChevronDown
                          size={24}
                          className={`text-[#b07a63] transition-transform duration-300 ${openSub === n.label ? "rotate-180" : ""}`}
                        />
                      </button>
                      <AnimatePresence initial={false}>
                        {openSub === n.label && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: EASE }}
                            className="overflow-hidden"
                          >
                            <div className="pb-4 pl-1 space-y-1">
                              {n.children.map((c) => (
                                <Link
                                  key={c.label}
                                  to={c.to}
                                  onClick={() => setOpen(false)}
                                  data-testid={`mobile-sub-${c.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                                  className="block py-2.5 text-lg text-[#4b2018]"
                                >
                                  {c.label}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : n.type === "route" ? (
                    <Link
                      to={n.to}
                      onClick={() => setOpen(false)}
                      className={linkClass}
                    >
                      {n.label}
                      <ArrowRight
                        size={22}
                        className="text-[#b07a63] opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                      />
                    </Link>
                  ) : (
                    <a
                      href={hashHref(n.to)}
                      onClick={() => setOpen(false)}
                      className={linkClass}
                    >
                      {n.label}
                      <ArrowRight
                        size={22}
                        className="text-[#b07a63] opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                      />
                    </a>
                  )}
                </motion.div>
              ))}
            </nav>

            <div className="px-8 pb-10">
              <a
                href={PLANITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-reserve"
                className="flex items-center justify-center gap-2 rounded-full bg-[#4e231b] text-white py-4 text-sm tracking-wide hover:bg-[#b07a63] transition-colors"
              >
                Prendre rendez-vous <ArrowRight size={16} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
